import { useApolloClient } from "@apollo/client";
import { getDataOrNull, getNameWithCopy } from "@msys/common";
import {
  EditableTypography,
  FormattedFloatInput,
  ListHeader,
  MenuButton,
  MenuItemWithIcon,
  ModalOpenButton,
  ModalOpenProcess,
  ModalOpenProcessRef,
} from "@msys/ui";
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutlined as DeleteOutlineIcon,
  NotesOutlined as NotesOutlineIcon,
} from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import { GridRowOrderChangeParams } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { NoPropagateBox } from "../../commons/NoPropagateBox.js";
import { BackButton } from "../../commons/button/BackButton.js";
import { ButtonCreate } from "../../commons/button/Button.js";
import { ButtonCircleWrapper } from "../../commons/button/ButtonCircleWrapper.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { usePaginationParamsInUrl } from "../../commons/pagination/usePaginationParams.js";
import { SearchItemInputModal } from "../../features/doc-items/SearchItemInputModal.js";
import { ProductsListView } from "../../features/products/components/ProductsListView.js";
import {
  ProductListItems_ProductListItemFragment,
  useAddProductsToProductListMutation,
  useProductListItemsQuery,
  useRemoveProductFromProductListMutation,
  useSetProductListItemsOrderMutation,
  useUpdateProductListItemQuantityMutation,
} from "../../features/productLists/ProductListItems.generated.js";
import { useUpdateProductListMutation } from "../../features/productLists/EditProductListModal.generated.js";
import { EditProductListDescriptionModal } from "../../features/productLists/EditProductListDescriptionModal.js";
import { ProductOverviewModal } from "../../features/products/modals/ProductOverviewModal.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import {
  useDeleteProductListMutation,
  useDuplicateProductListMutation,
} from "./ProductLists.generated.js";
import { useProductListOverviewQuery } from "./ProductListOverview.generated.js";

const ALLOWED_VIEWS: CollectionView[] = ["gallery", "list", "table"];

/**
 * A product-list item row for `ProductsListView`: the live-resolved PIM product
 * plus the stored item metadata (id/quantity/rank). Rows are keyed by
 * `productListItemId` since the same product may appear more than once.
 */
type ProductListItemRow = NonNullable<
  ProductListItems_ProductListItemFragment["product"]
> & {
  productListItemId: string;
  quantity: number;
  rank: number;
  __reorder__: string;
};

interface Props {
  submenuItems: PageTopbarItem[];
  pathToProductListsList: string;
}

export const ProductListOverview = ({
  submenuItems,
  pathToProductListsList,
}: Props) => {
  const { id } = useParams();
  if (!id) throw new Error("ProductList id is missing");

  const { t } = useTranslate(["ProductLists", "Global"]);

  const client = useApolloClient();
  const navigate = useNavigate();

  const editDescriptionModalRef = React.useRef<ModalOpenProcessRef>(null);

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "product-list-overview",
      "table",
      "list"
    );

  const listQuery = useProductListOverviewQuery({ client, variables: { id } });
  const productList =
    getDataOrNull((listQuery.data ?? listQuery.previousData)?.productLists)
      ?.edges[0]?.node ?? null;

  const itemsQuery = useProductListItemsQuery({
    client,
    variables: { productListId: id },
  });
  const itemsData = getDataOrNull(
    (itemsQuery.data ?? itemsQuery.previousData)?.productListItems
  );
  const items = [...(itemsData?.edges.map(e => e.node) ?? [])].sort(
    (a, b) => a.rank - b.rank
  );

  const [addProducts, { loading: addProductsLoading }] =
    useAddProductsToProductListMutation({
      client,
      awaitRefetchQueries: true,
      refetchQueries: [
        namedOperations.Query.ProductListItems,
        namedOperations.Query.ProductListOverview,
      ],
    });

  const [duplicateProductList] = useDuplicateProductListMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      namedOperations.Query.ProductLists,
      namedOperations.Query.ProductListsAuthors,
    ],
  });

  const [deleteProductList] = useDeleteProductListMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      namedOperations.Query.ProductLists,
      namedOperations.Query.ProductListsAuthors,
    ],
  });

  const [updateProductList] = useUpdateProductListMutation({ client });

  const breadcrumbs = React.useMemo(
    () =>
      productList
        ? [
            {
              title: t("Product lists", { ns: "ProductLists" }),
              to: pathToProductListsList,
            },
            {
              title: productList.title,
              to: `${pathToProductListsList}/${productList.id}`,
            },
          ]
        : undefined,
    [pathToProductListsList, t, productList]
  );

  return (
    <Page
      title={productList?.title ?? ""}
      subtitle={t("Product lists", { ns: "ProductLists" })}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
    >
      {productList && (
        <PageContainer>
          <ListHeader
            TitleButton={<BackButton to={pathToProductListsList} />}
            title={
              <EditableTypography
                variant="inherit"
                value={productList.title}
                allowEmpty={false}
                onChange={async newTitle => {
                  if (newTitle !== productList.title) {
                    await updateProductList({
                      variables: {
                        input: {
                          productListId: productList.id,
                          values: { title: newTitle },
                        },
                      },
                    });
                  }
                }}
              />
            }
            SwitchViewButton={
              <SwitchCollectionViewButton
                allowedViews={ALLOWED_VIEWS}
                activeView={activeView}
                setActiveView={setActiveView}
              />
            }
            MenuButton={
              <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
                <>
                  <MenuButton>
                    <MenuItemWithIcon
                      icon={<NotesOutlineIcon />}
                      onClick={() => editDescriptionModalRef.current?.open()}
                    >
                      {t("Edit description", { ns: "ProductLists" })}
                    </MenuItemWithIcon>
                    <ModalOpenButton
                      Modal={ConfirmModal}
                      modalProps={{
                        title: t(
                          "Are you sure you want to duplicate this product list?",
                          { ns: "ProductLists" }
                        ),
                        handleConfirm: async () => {
                          const res = await duplicateProductList({
                            variables: {
                              input: {
                                productListId: productList.id,
                                title: getNameWithCopy(
                                  productList.title,
                                  t("copy", { ns: "Global" })
                                ),
                              },
                            },
                          });
                          const newId =
                            res.data?.duplicateProductList.productList.id;
                          if (newId) {
                            navigate(`${pathToProductListsList}/${newId}`);
                          }
                        },
                      }}
                    >
                      <MenuItemWithIcon icon={<ContentCopyIcon />}>
                        {t("Duplicate", { ns: "ProductLists" })}
                      </MenuItemWithIcon>
                    </ModalOpenButton>
                    <ModalOpenButton
                      Modal={ConfirmModal}
                      modalProps={{
                        title: t(
                          "Are you sure you want to remove this product list?",
                          { ns: "ProductLists" }
                        ),
                        handleConfirm: async () => {
                          await deleteProductList({
                            variables: {
                              input: { productListId: productList.id },
                            },
                          });
                          navigate(pathToProductListsList);
                        },
                      }}
                    >
                      <MenuItemWithIcon icon={<DeleteOutlineIcon />}>
                        {t("Delete", { ns: "Global" })}
                      </MenuItemWithIcon>
                    </ModalOpenButton>
                  </MenuButton>
                  <ModalOpenProcess
                    ref={editDescriptionModalRef}
                    Modal={EditProductListDescriptionModal}
                    modalProps={{
                      productListId: productList.id,
                      description: productList.description,
                    }}
                  />
                </>
              </RestrictedByOrganisationPermissionWithDebug>
            }
            CreateButton={
              <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
                <ModalOpenButton
                  Modal={SearchItemInputModal}
                  modalProps={{
                    initialSearchTab: "products",
                    initialSearchValue: "",
                    allowedSearchTabs: ["products"],
                    showQuantityInput: true,
                    AddToListIcon: AddIcon,
                    handleSelect: async ({ products }) => {
                      const productInputs = products.flatMap(
                        ({ quantity, product }) => {
                          const supplierId =
                            "__typename" in product
                              ? product.supplierId
                              : product.productSupplierId;
                          const articleNumber =
                            "__typename" in product
                              ? product.articleNumber
                              : product.productArticleNumber;
                          return supplierId && articleNumber
                            ? [{ supplierId, articleNumber, quantity }]
                            : [];
                        }
                      );
                      if (productInputs.length === 0) return;
                      await addProducts({
                        variables: {
                          input: {
                            productListId: productList.id,
                            products: productInputs,
                          },
                        },
                      });
                    },
                  }}
                >
                  <ButtonCreate
                    title={t("Add products", { ns: "ProductLists" })}
                    disabled={addProductsLoading}
                  />
                </ModalOpenButton>
              </RestrictedByOrganisationPermissionWithDebug>
            }
            sx={{ mb: 2 }}
          />
          <ProductListItemsGrid
            productListId={productList.id}
            pathToProductList={`${pathToProductListsList}/${productList.id}`}
            items={items}
            loading={itemsQuery.loading}
            activeView={activeView}
          />
        </PageContainer>
      )}
      <Outlet />
    </Page>
  );
};

function ProductListItemsGrid({
  productListId,
  pathToProductList,
  items,
  loading,
  activeView,
}: {
  productListId: string;
  pathToProductList: string;
  items: ProductListItems_ProductListItemFragment[];
  loading: boolean;
  activeView: CollectionView;
}) {
  const { t } = useTranslate(["ProductLists", "Global"]);
  const client = useApolloClient();
  const stateStore = useDataGridStateStore("ProductListOverview-Items");
  const { paginationModel, setPaginationModel } = usePaginationParamsInUrl();

  const [setItemsOrder, { loading: setOrderLoading }] =
    useSetProductListItemsOrderMutation({
      client,
      awaitRefetchQueries: true,
      refetchQueries: [namedOperations.Query.ProductListItems],
    });

  const rows = React.useMemo<ProductListItemRow[]>(
    () =>
      items.flatMap(item =>
        item.product
          ? [
              {
                ...item.product,
                productListItemId: item.id,
                quantity: item.quantity,
                rank: item.rank,
                __reorder__: item.product.texts?.title ?? item.articleNumber,
              },
            ]
          : []
      ),
    [items]
  );

  // The gallery/list branches of `ProductsListView` render every row they are
  // given, so they have to be handed the current page; the table paginates the
  // full array itself.
  const visibleRows = React.useMemo(
    () =>
      activeView === "table"
        ? rows
        : rows.slice(
            paginationModel.page * paginationModel.pageSize,
            (paginationModel.page + 1) * paginationModel.pageSize
          ),
    [rows, activeView, paginationModel.page, paginationModel.pageSize]
  );

  const handleRowOrderChange = React.useCallback(
    async (params: GridRowOrderChangeParams) => {
      const reordered = [...rows];
      const [moved] = reordered.splice(params.oldIndex, 1);
      reordered.splice(params.targetIndex, 0, moved);
      await setItemsOrder({
        variables: {
          input: {
            productListId,
            productListItemIds: reordered.map(row => row.productListItemId),
          },
        },
      });
    },
    [rows, setItemsOrder, productListId]
  );

  const TableActions = React.useCallback(
    ({ product }: { product: ProductListItemRow }) => (
      <ProductListItemActions product={product} view="table" />
    ),
    []
  );

  const CardActionButton = React.useCallback(
    ({ product }: { product: ProductListItemRow }) => (
      <ProductListItemActions product={product} view={activeView} />
    ),
    [activeView]
  );

  return (
    <ProductsListView<ProductListItemRow>
      activeView={activeView}
      items={visibleRows}
      totalCount={rows.length}
      loading={loading || setOrderLoading}
      stateStore={stateStore}
      tableActionsWidth={160}
      CardActionButton={CardActionButton}
      TableActions={TableActions}
      getRowId={row => row.productListItemId}
      getProductTo={row =>
        `${pathToProductList}/product/${row.supplierId}/${encodeURIComponent(
          row.articleNumber
        )}`
      }
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      dataGridProps={{
        rowReordering: true,
        onRowOrderChange: handleRowOrderChange,
        hideFooter: false,
        localeText: {
          noRowsLabel: t("There are no items to display", { ns: "Global" }),
          footerTotalRows: `${t("Items", { ns: "ProductLists" })}:`,
        },
      }}
    />
  );
}

function ProductListItemActions({
  product,
  view,
}: {
  product: ProductListItemRow;
  view: CollectionView;
}) {
  const { t } = useTranslate(["ProductLists"]);
  const client = useApolloClient();

  const [removeProduct] = useRemoveProductFromProductListMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      namedOperations.Query.ProductListItems,
      namedOperations.Query.ProductListOverview,
    ],
  });

  const deleteButton = (
    <ModalOpenButton
      Modal={ConfirmModal}
      modalProps={{
        title: t(
          "Are you sure you want to remove this product from the list?",
          { ns: "ProductLists" }
        ),
        handleConfirm: async () => {
          await removeProduct({
            variables: {
              input: { productListItemId: product.productListItemId },
            },
          });
        },
      }}
    >
      <IconButton
        size="small"
        color="primary"
        aria-label={t("Remove product from list", { ns: "ProductLists" })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <DeleteOutlineIcon />
      </IconButton>
    </ModalOpenButton>
  );

  return (
    <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        {/* Gallery cards have no room for a quantity editor; the list view uses
            a compact one, the table its default width. */}
        {view !== "gallery" && (
          <NoPropagateBox>
            <ProductListItemQuantityInput
              productListItemId={product.productListItemId}
              quantity={product.quantity}
              sx={view === "list" ? { width: 56 } : undefined}
            />
          </NoPropagateBox>
        )}
        {view === "table" ? (
          deleteButton
        ) : (
          <ButtonCircleWrapper>{deleteButton}</ButtonCircleWrapper>
        )}
      </Stack>
    </RestrictedByOrganisationPermissionWithDebug>
  );
}

function ProductListItemQuantityInput({
  productListItemId,
  quantity,
  sx,
}: {
  productListItemId: string;
  quantity: number;
  sx?: React.ComponentProps<typeof FormattedFloatInput>["sx"];
}) {
  const client = useApolloClient();
  const [value, setValue] = React.useState<number>(quantity);

  React.useEffect(() => {
    setValue(quantity);
  }, [quantity]);

  const [updateQuantity] = useUpdateProductListItemQuantityMutation({ client });

  const commit = React.useCallback(async () => {
    // positive-value validation: reset to the stored quantity on invalid input
    if (value <= 0) {
      setValue(quantity);
      return;
    }
    if (value !== quantity) {
      await updateQuantity({
        variables: { input: { productListItemId, quantity: value } },
      });
    }
  }, [value, quantity, productListItemId, updateQuantity]);

  return (
    <FormattedFloatInput
      value={value}
      onChange={newValue => {
        if (newValue !== null) setValue(newValue);
      }}
      min={0}
      size="extra-small"
      sx={sx}
      onBlur={commit}
      onKeyPress={async e => {
        e.stopPropagation();
        if (e.key === "Enter") await commit();
      }}
    />
  );
}

/**
 * Route-driven modal: the product overview is shown whenever the URL matches
 * `.../product/:supplierId/:articleNumber`; closing navigates back to the list.
 */
export const ProductListItemOverviewModal = ({
  pathToProductListsList,
}: {
  pathToProductListsList: string;
}) => {
  const { t } = useTranslate(["Product"]);
  const { id, supplierId, articleNumber } = useParams();
  const navigate = useNavigate();

  if (!id || !supplierId || !articleNumber) return null;

  // The modal's built-in "Catalogue" button calls handleClose() right after
  // navigating, which would clobber our route-based close (navigate to the
  // list). Provide the catalogue navigation explicitly so it takes effect.
  return (
    <ProductOverviewModal
      productSupplierId={supplierId}
      productArticleNumber={articleNumber}
      actionButtonProps={{
        label: t("Catalogue", { ns: "Product" }),
        handleClick: () => {
          navigate(
            `/products/catalogues/supplier/${supplierId}/${encodeURIComponent(
              articleNumber
            )}/overview`
          );
        },
        buttonProps: { color: "primary" as const, variant: "text" as const },
      }}
      handleClose={() => navigate(`${pathToProductListsList}/${id}`)}
    />
  );
};
